
import type { QueriesByCategoryTotalsData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals-data'
import type { QueriesByCategoryTotalsResponse } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals.response'

import type { PropType, SetupContext } from 'vue'
import type {
  CategoriesPath,
  CategoriesTreeItem,
  CategoriesTreeItemRadio,
  Dictionary,
  HighlightsFilterSetup,
} from '../../../models'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { computed, defineComponent, getCurrentInstance, onMounted, reactive, ref } from 'vue'

import { mapActions } from '../../../composables/utils'

import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'

import EpbAside from '../../aside.component.vue'
import { UiAlert } from '../../keen-ui'
import { EpbButton } from '../../shared/epb-button'

import CategoriesItem from './categories-item.component.vue'

export default defineComponent({
  name: 'CategoriesAside',
  components: {
    EpbAside,
    UiAlert,
    CategoriesItem,
    EpbButton,
  },
  props: {
    categoriesPath: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    excludedCategories: {
      type: Array as PropType<Array<HighlightsFilterSetup>>,
      default: () => [],
    },
    period: {
      type: Object as PropType<{ startDate: Date; endDate: Date }>,
      required: true,
    },
    startOpened: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<Dictionary>,
      required: true,
    },
  },
  setup(props: Record<string, any>, ctx: SetupContext) {
    const { emit } = ctx
    const {
      proxy: { $store },
    } = getCurrentInstance()

    const baseClass = 'epb-categories-aside'

    const opened = ref(props.startOpened)
    const hideUiAlert = ref(false)

    const treeCategory = reactive({
      id: '_root_',
      level: 0,
      selected: false,
      children: [],
    } as CategoriesTreeItemRadio)

    const { fetchStatsReport } = mapActions($store, ANALYTICS_CONSTS.moduleName, {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
    })

    const findChildSelected = function (
      prevTreeCategory: CategoriesTreeItemRadio,
    ): CategoriesPath[] {
      const childSelected = prevTreeCategory.children.find(
        (child: CategoriesTreeItemRadio) => child.selected,
      )
      if (!childSelected) {
        return []
      }

      return [
        {
          id: childSelected.id,
          name: childSelected.name,
        },
        ...findChildSelected(childSelected),
      ]
    }

    const localCategoriesPath = computed((): Array<CategoriesPath> => {
      return findChildSelected(treeCategory)
    })

    const lastCategorySelected = computed((): CategoriesTreeItem => {
      if (!localCategoriesPath.value.length) {
        return
      }
      const path = [...localCategoriesPath.value]
      const level = path.length
      const lastCategory = path.pop()
      const parentLastCategory = path.pop()

      return {
        level,
        id: lastCategory?.id,
        parentId: parentLastCategory?.id,
      }
    })

    const updateTreeCategory = function (
      category: CategoriesTreeItemRadio,
      prevTreeCategory: CategoriesTreeItemRadio,
      queriesByCategoryTotalsResponse,
    ): CategoriesTreeItemRadio {
      if (category.level === prevTreeCategory.level) {
        if (category.id === prevTreeCategory.id && !category.selected) {
          return {
            ...prevTreeCategory,
            selected: true,
            children: queriesByCategoryTotalsResponse.map(queryCat => {
              return {
                id: queryCat.categoryId,
                name: queryCat.categoryName,
                level: prevTreeCategory.level + 1,
                parentId: prevTreeCategory.id,
                selected: false,
              }
            }),
          }
        }

        return {
          ...prevTreeCategory,
          selected: false,
        }
      }

      if (prevTreeCategory.children) {
        return {
          ...prevTreeCategory,
          children: prevTreeCategory.children.map(child => {
            return updateTreeCategory(category, child, queriesByCategoryTotalsResponse)
          }),
        }
      }

      return prevTreeCategory
    }

    const compareArrayValues = (listA: string[], listB: string[]): boolean => {
      return Object.values(listA).toString() === Object.values(listB).toString()
    }

    const formNotUpdated = computed((): boolean => {
      return compareArrayValues(
        props.categoriesPath,
        localCategoriesPath.value.map(path => path.id),
      )
    })

    const formHasInvalidCategories = computed((): boolean => {
      if (formNotUpdated.value) {
        return false
      }

      return props.excludedCategories.some(
        (categoryNotAvailable: HighlightsFilterSetup): boolean => {
          return compareArrayValues(
            categoryNotAvailable.categoriesPath,
            localCategoriesPath.value.map(path => path.id),
          )
        },
      )
    })

    const close = (): void => {
      opened.value = false
      emit('close')
    }

    const accept = (): void => {
      hideUiAlert.value = true
      if (!formNotUpdated.value) {
        emit('accept', localCategoriesPath.value)
      }
      close()
    }

    const fetchData = async function (
      category: CategoriesTreeItemRadio,
    ): Promise<QueriesByCategoryTotalsData[]> {
      const params = {
        ...props.period,
        level: category.level + 1,
        ...(!!category.level && { parentId: category.id }),
        rows: 0,
      }
      const queriesByCategoryTotalsResponse: QueriesByCategoryTotalsResponse =
        await fetchStatsReport({
          id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS,
          params,
        })
      return queriesByCategoryTotalsResponse.data.sort(
        (a: QueriesByCategoryTotalsData, b: QueriesByCategoryTotalsData): number =>
          a.categoryName.localeCompare(b.categoryName) || a.categoryId.localeCompare(b.categoryId),
      )
    }

    const selectCategory = async (
      category: CategoriesTreeItemRadio,
      prevTreeCategory: CategoriesTreeItemRadio,
    ): Promise<CategoriesTreeItemRadio> => {
      const queriesByCategoryTotalsResponse = category.selected ? [] : await fetchData(category)

      return updateTreeCategory(category, prevTreeCategory, queriesByCategoryTotalsResponse)
    }

    const clickCategory = async (category: CategoriesTreeItemRadio): Promise<void> => {
      const updatedTreeCategory = await selectCategory(category, treeCategory)
      Object.assign(treeCategory, updatedTreeCategory)
    }

    onMounted(async (): Promise<void> => {
      const rootCategory: CategoriesTreeItemRadio = {
        id: '_root_',
        name: 'root',
        level: 0,
        selected: false,
      }
      const queriesByCategoryTotalsResponse = await fetchData(rootCategory)

      let updatedTreeCategory = updateTreeCategory(
        rootCategory,
        treeCategory,
        queriesByCategoryTotalsResponse,
      )

      for (const id of props.categoriesPath) {
        const i: number = props.categoriesPath.indexOf(id)
        const category: CategoriesTreeItemRadio = {
          id,
          ...(i > 0 && { parentId: props.categoriesPath[i - 1] }),
          level: i + 1,
          selected: false,
        }
        updatedTreeCategory = await selectCategory(category, updatedTreeCategory)
      }
      Object.assign(treeCategory, updatedTreeCategory)
    })

    return {
      baseClass,
      opened,
      formHasInvalidCategories,
      localCategoriesPath,
      lastCategorySelected,
      treeCategory,
      hideUiAlert,
      close,
      clickCategory,
      accept,
    }
  },
})
